import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, Map as MapIcon, List, X, Car, MapPin, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Navbar } from '@/components/Navbar';
import { ListingCard } from '@/components/ListingCard';
import { MapComponent } from '@/components/MapComponent';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

// Helper function to calculate distance between two points (Haversine formula)
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in meters
};

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [listings, setListings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('split');
  const [selectedListing, setSelectedListing] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [userLocation, setUserLocation] = useState(null);
  const [showRadiusFilter, setShowRadiusFilter] = useState(false);
  
  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };
  
  // Filters - radius options: 200m, 500m, 1km, 2km (max 2km, min 100m)
  const [filters, setFilters] = useState({
    maxPrice: 50,
    vehicleSize: '',
    radius: 1000,
    radiusEnabled: true
  });
  
  // Map center (Sydney CBD default) - use ref to avoid re-renders
  const [mapCenter, setMapCenter] = useState({ lat: -33.8688, lng: 151.2093 });
  const mapCenterRef = useRef(mapCenter);
  const filtersRef = useRef(filters);

  // Keep refs in sync
  useEffect(() => {
    mapCenterRef.current = mapCenter;
  }, [mapCenter]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Check auth on mount
  useEffect(() => {
    const headers = getAuthHeaders();
    axios.get(`${API}/auth/me`, { withCredentials: true, headers })
      .then(res => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  // Get user location on mount and center map there
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLoc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(userLoc);
          setMapCenter(userLoc);
          mapCenterRef.current = userLoc;
          // Fetch listings for user's location
          fetchListings();
        },
        (error) => {
          console.log('Geolocation error:', error);
          // Keep Sydney as default if geolocation fails
          fetchListings();
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      fetchListings();
    }
  }, []);

  useEffect(() => {
    if (user) {
      const headers = getAuthHeaders();
      axios.get(`${API}/favorites`, { withCredentials: true, headers })
        .then(res => setFavorites(res.data.favorites?.map(f => f.listing_id) || []))
        .catch(() => {});
    }
  }, [user]);

  const toggleFavorite = async (listingId) => {
    if (!user) {
      toast.error('Please sign in to save spots');
      return;
    }
    try {
      const headers = getAuthHeaders();
      if (favorites.includes(listingId)) {
        await axios.delete(`${API}/favorites/${listingId}`, { withCredentials: true, headers });
        setFavorites(favorites.filter(id => id !== listingId));
        toast.success('Removed from saved');
      } else {
        await axios.post(`${API}/favorites/${listingId}`, {}, { withCredentials: true, headers });
        setFavorites([...favorites, listingId]);
        toast.success('Saved to favorites');
      }
    } catch (err) {
      toast.error('Failed to update favorites');
    }
  };

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('lat', mapCenterRef.current.lat);
      params.append('lng', mapCenterRef.current.lng);
      params.append('radius', filtersRef.current.radius);
      params.append('max_price', filtersRef.current.maxPrice);
      if (filtersRef.current.vehicleSize) params.append('vehicle_size', filtersRef.current.vehicleSize);
      params.append('limit', 50);

      const res = await axios.get(`${API}/listings?${params.toString()}`);
      setListings(res.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch when filters change (but not on initial mount - that's handled by geolocation)
  useEffect(() => {
    // Skip initial render
    if (userLocation !== null || listings.length > 0) {
      fetchListings();
    }
  }, [filters]);

  // Filter listings by radius (client-side filtering based on user location)
  const filteredListings = useMemo(() => {
    if (!filters.radiusEnabled || !userLocation) {
      return listings;
    }
    
    return listings.filter(listing => {
      const coords = listing.location?.coordinates;
      if (!coords) return false;
      
      const distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        coords[1], // latitude
        coords[0]  // longitude
      );
      
      return distance <= filters.radius;
    });
  }, [listings, filters.radiusEnabled, filters.radius, userLocation]);

  // Geocode search query
  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      const res = await axios.post(`${API}/geocode/forward?address=${encodeURIComponent(searchQuery)}`);
      if (res.data.results?.length > 0) {
        const { lat, lng } = res.data.results[0].coordinates;
        setMapCenter({ lat, lng });
        mapCenterRef.current = { lat, lng };
        fetchListings();
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
  };

  const handleMarkerClick = (listing) => {
    setSelectedListing(listing);
    const element = document.getElementById(`listing-${listing.listing_id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleViewDetails = (listing) => {
    navigate(`/listing/${listing.listing_id}`);
  };

  // Filter Panel
  const FilterPanel = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label>Max Price per Hour: ${filters.maxPrice}</Label>
        <Slider
          value={[filters.maxPrice]}
          onValueChange={([value]) => setFilters(f => ({ ...f, maxPrice: value }))}
          max={100}
          min={1}
          step={1}
          className="w-full"
        />
      </div>

      <div className="space-y-3">
        <Label>Vehicle Size</Label>
        <Select
          value={filters.vehicleSize || "any"}
          onValueChange={(value) => setFilters(f => ({ ...f, vehicleSize: value === "any" ? "" : value }))}
        >
          <SelectTrigger data-testid="filter-vehicle-size">
            <SelectValue placeholder="Any size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any size</SelectItem>
            <SelectItem value="small">Small</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="large">Large</SelectItem>
            <SelectItem value="oversized">Oversized</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <Target className="w-4 h-4 text-[#5ebc67]" />
            Search Radius
          </Label>
          <button
            type="button"
            onClick={() => setFilters(f => ({ ...f, radiusEnabled: !f.radiusEnabled }))}
            className={`relative w-10 h-5 rounded-full transition-colors ${
              filters.radiusEnabled ? 'bg-[#5ebc67]' : 'bg-slate-200'
            }`}
            data-testid="radius-toggle"
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              filters.radiusEnabled ? 'translate-x-5' : ''
            }`} />
          </button>
        </div>
        
        {filters.radiusEnabled && (
          <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: 200, label: '200m' },
                { value: 500, label: '500m' },
                { value: 1000, label: '1km' },
                { value: 2000, label: '2km' }
              ].map(({ value, label }) => (
                <Button
                  key={value}
                  type="button"
                  variant={filters.radius === value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setFilters(f => ({ ...f, radius: value }));
                  }}
                  className={filters.radius === value ? 'bg-[#5ebc67] hover:bg-[#4da855]' : ''}
                  data-testid={`radius-${value}`}
                >
                  {label}
                </Button>
              ))}
            </div>
            
            {!userLocation && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Enable location to use radius filter
              </p>
            )}
          </div>
        )}
      </div>

      <Button
        onClick={() => {
          setFilters({ maxPrice: 50, vehicleSize: '', radius: 1000, radiusEnabled: true });
        }}
        variant="outline"
        className="w-full"
        data-testid="clear-filters-btn"
      >
        Clear Filters
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      {/* Search Header */}
      <div className="fixed top-16 md:top-20 left-0 right-0 bg-white border-b border-slate-200 z-40 py-4">
        <div className="container-main">
          <div className="flex items-center gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 rounded-full border-slate-200"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full h-12 px-6" data-testid="search-btn">
                Search
              </Button>
            </form>

            {/* Filters - Desktop */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="hidden md:flex items-center gap-2 rounded-full h-12" data-testid="filters-btn">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <FilterPanel />
                </div>
              </SheetContent>
            </Sheet>

            {/* View Toggle - Desktop */}
            <div className="hidden md:flex items-center border border-slate-200 rounded-full p-1">
              <Button
                variant={view === 'split' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-full"
                onClick={() => setView('split')}
                data-testid="view-split-btn"
              >
                Split
              </Button>
              <Button
                variant={view === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-full"
                onClick={() => setView('list')}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={view === 'map' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-full"
                onClick={() => setView('map')}
                data-testid="view-map-btn"
              >
                <MapIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-36 md:pt-40">
        {/* Mobile Filters */}
        <div className="md:hidden px-4 pb-4">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="w-full flex items-center gap-2" data-testid="mobile-filters-btn">
                <SlidersHorizontal className="w-4 h-4" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[80vh]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterPanel />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Results Count */}
        <div className="container-main mb-4">
          <p className="text-sm text-slate-600">
            {loading ? 'Searching...' : (
              filters.radiusEnabled && userLocation
                ? `${filteredListings.length} parking spots within ${filters.radius >= 1000 ? (filters.radius / 1000) + ' km' : filters.radius + ' m'}`
                : `${filteredListings.length} parking spots found`
            )}
          </p>
        </div>

        {/* Split View */}
        <div className={`flex ${view === 'list' ? 'flex-col' : ''}`}>
          {/* Listings Panel */}
          {(view === 'split' || view === 'list') && (
            <div className={`${view === 'split' ? 'w-full md:w-1/2 lg:w-2/5' : 'w-full'} h-[calc(100vh-10rem)] overflow-y-auto px-4 md:px-6`}>
              {loading ? (
                <div className="grid gap-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-48 bg-slate-200 rounded-2xl animate-pulse"></div>
                  ))}
                </div>
              ) : filteredListings.length === 0 ? (
                <div className="text-center py-12">
                  <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No spots found</h3>
                  <p className="text-slate-500">
                    {filters.radiusEnabled ? 'Try increasing the radius or disable the filter' : 'Try adjusting your filters or search area'}
                  </p>
                </div>
              ) : (
                <div className={`grid gap-4 ${view === 'list' ? 'md:grid-cols-2 lg:grid-cols-3' : ''}`}>
                  {filteredListings.map(listing => (
                    <div
                      key={listing.listing_id}
                      id={`listing-${listing.listing_id}`}
                      className={selectedListing?.listing_id === listing.listing_id ? 'ring-2 ring-[#5ebc67] rounded-2xl' : ''}
                    >
                      <ListingCard
                        listing={listing}
                        onClick={() => handleViewDetails(listing)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Map Panel */}
          {(view === 'split' || view === 'map') && (
            <div className={`${view === 'split' ? 'hidden md:block md:w-1/2 lg:w-3/5' : 'w-full'} h-[calc(100vh-10rem)] sticky top-40`}>
              <MapComponent
                listings={filteredListings}
                center={mapCenter}
                selectedListingId={selectedListing?.listing_id}
                onMarkerClick={handleMarkerClick}
                showUserLocation={true}
                radiusMeters={filters.radiusEnabled ? filters.radius : null}
                radiusCenter={userLocation}
                onUserLocationChange={setUserLocation}
              />
            </div>
          )}
        </div>

        {/* Mobile View Toggle */}
        <div className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-lg">
            <Button
              variant={view === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-full"
              onClick={() => setView('list')}
            >
              <List className="w-4 h-4 mr-2" />
              List
            </Button>
            <Button
              variant={view === 'map' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-full"
              onClick={() => setView('map')}
            >
              <MapIcon className="w-4 h-4 mr-2" />
              Map
            </Button>
          </div>
        </div>

        {/* Selected Listing Preview (Mobile Map View) */}
        {view === 'map' && selectedListing && (
          <div className="md:hidden fixed bottom-20 left-4 right-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl p-4 border border-slate-100">
              <button
                className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600"
                onClick={() => setSelectedListing(null)}
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex gap-4">
                <img
                  src={selectedListing.photos?.[0] || 'https://images.unsplash.com/photo-1571799923032-df849d54bcb9?w=200'}
                  alt={selectedListing.title}
                  className="w-24 h-24 rounded-xl object-cover"
                />
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">{selectedListing.title}</h3>
                  <p className="text-sm text-slate-500 mb-2">{selectedListing.suburb}</p>
                  <p className="text-lg font-bold text-[#5ebc67]">${selectedListing.price_hourly}/hr</p>
                </div>
              </div>
              <Button
                className="w-full mt-4 bg-[#5ebc67] hover:bg-[#4da855] rounded-full"
                onClick={() => handleViewDetails(selectedListing)}
              >
                View Details
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
